import type { PointerEvent as ReactPointerEvent } from 'react'
import { FROZEN_OBJECT_CURSOR } from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import type { ResizeHandle } from '@/lib/boundaries/bounding-box-boundary'
import type { SelectionChromePreview } from '@/lib/shapes/selection-chrome-preview'
import { getResizeCursorForHandle } from '@/lib/transforms/selection-resize-cursor'

import { resolveEdgeHitQuad } from '../helpers/edge-hit-quad'

/**
 * The four sides, as index pairs into `geometry.corners` plus the resize handle each one drives.
 * `rotatedRectCorners` emits NW, NE, SE, SW in that order (`shape-coordinates.ts`).
 */
const EDGE_HIT_SPECS = [
  { from: 0, to: 1, key: 'n' },
  { from: 1, to: 2, key: 'e' },
  { from: 2, to: 3, key: 's' },
  { from: 3, to: 0, key: 'w' },
] as const satisfies ReadonlyArray<{ from: number; to: number; key: ResizeHandle }>

/**
 * Edge resize strips (WMPCM-9868). Before this the frame's SIDES only ever moved the
 * selection — the sole way to resize was to hit one of the eight nodes, and a text frame's
 * n/s nodes are exactly where its link ports sit, so users reported being unable to resize
 * at all. Dragging an edge now resizes along that edge's axis.
 *
 * Ordering is the whole mechanism: these paint AFTER the move polygon, so they take its
 * hit-testing along the sides, and BEFORE the corner and midpoint nodes, so those still win
 * where they overlap. The interior keeps moving the selection, which is what a `fill="none"`
 * polygon with `pointerEvents: all` gives us.
 *
 * Screen px throughout — `chromeGeometry` has already projected.
 */
export function ChromeEdgeStrips({
  geometry,
  geometryInactive,
  beginResize,
}: {
  geometry: SelectionChromePreview
  geometryInactive: boolean
  beginResize: (key: ResizeHandle) => (event: ReactPointerEvent<SVGElement>) => void
}) {
  return (
    <>
      {EDGE_HIT_SPECS.map(({ from, to, key }) => {
        const quad = resolveEdgeHitQuad(geometry.corners[from], geometry.corners[to])

        if (!quad) return null

        return (
          <polygon
            key={`edge-${key}`}
            data-resize-edge={key}
            points={quad}
            fill="transparent"
            style={{
              pointerEvents: 'all',
              cursor: geometryInactive
                ? FROZEN_OBJECT_CURSOR
                : getResizeCursorForHandle(key, geometry.rotationDeg),
            }}
            onPointerDown={beginResize(key)}
          />
        )
      })}
    </>
  )
}
